// src/presentation/pages/PatientDetails.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PermissionGuard } from "../components/PermissionGuard";
import { usePatientPermissions } from "../../application/hooks/usePatientPermissions";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useGetPatientByIdQuery,
  useGetPatientAssessmentsQuery,
  useCreateAccessibilitySensoryMutation,
  useGetPatientAssessmentDetailQuery,
  useCreateCurrentAssessmentMutation,
} from "../../infrastructure/api/patientsApi";
import { useSubmitCaregiverQuestionnaireMutation } from "../../infrastructure/api/caregiverQuestionnaireApi";
import type { AssessmentDetailDTO } from "../../domain/dto/ClinicalExaminationDTO";
import { toast } from "sonner";
import { CircleX, Loader2, MoreVertical } from "lucide-react";
import {
  formatDateTimeShort,
  formatTimeUntilStart,
  hasAppointmentStarted,
  isAppointmentInProgress,
} from "@/lib/dateUtils";
import AssignmentIcon from "@/assets/icons/assignment.svg?react";
import Step1SensoryAssessment from "./ClinicalExamination/Step1SensoryAssessment";
import Step2NeurologicalHistory from "./ClinicalExamination/Step2NeurologicalHistory";
import type {
  SensoryFormData,
  NeurologicalHistoryFormData,
  BehavioralFormData,
  CognitiveFunctionalFormData,
  ScreeningFormData,
} from "./ClinicalExamination/types";
import {
  initialSensoryFormData,
  initialNeurologicalFormData,
  initialBehavioralFormData,
  initialCognitiveFunctionalFormData,
  initialScreeningFormData,
} from "./ClinicalExamination/types";
import Step3BehavioralAssessment from "./ClinicalExamination/Step3BehavioralAssessment";
import Step4CognitiveFunctional from "./ClinicalExamination/Step4CognitiveFunctional";
import ScheduleScreeningDialog from "../components/ScheduleScreeningDialog";
import ScheduleReportReviewDialog from "../components/ScheduleReportReviewDialog";
import ReportGenerationBanner from "../components/ReportGenerationBanner";
import { Separator } from "@/components/ui/separator";
import CallIcon from "@/assets/icons/call.svg?react";
import MailIcon from "@/assets/icons/mail.svg?react";
import UserIcon from "@/assets/icons/User.svg?react";
import DeleteIcon from "@/assets/icons/delete.svg?react";
import EditIcon from "@/assets/icons/edit.svg?react";
import Step5Screening from "./ClinicalExamination/Step5Screening";
import { EditPatientDialog } from "../components/EditPatientDialog";
import { DeletePatientDialog } from "../components/DeletePatientDialog";
import { CancelScreeningApporintmentDialog } from "../components/CancelScreeningApporintmentDialog";
import { StopScreeningDialog } from "../components/StopScreeningDialog";
import RescheduleScreeningDialog from "../components/RescheduleScreeningDialog";
import AssessmentList from "../components/AssessmentList";
import { AssessmentStatusPill } from "@/components/AssessmentStatusPill";
import ClinicalExaminationDetails from "./ClinicalExamination/ClinicalExaminationDetails";
import type { Assessment } from "../../domain/entities/Patient";
import {
  DISABLE_ACTION_STATUSES,
  HIDE_CLINICAL_EXAM_STATUSES,
  NON_RETAKE_SCREENING_STATUSES,
  CREATE_NEW_ASSESSMENT_STATUSES,
  getStatusReason,
} from "../../infrastructure/data/PatientDetailsStatus";
import { REPORT_POLLING_STATUSES } from "../../infrastructure/data/reportGeneration";
import {
  REPORT_FAILED_STATUS,
  REPORT_GENERATING_STATUS,
  REPORT_PENDING_STATUSES,
} from "../../infrastructure/data/reportGeneration";
import { textStyles } from "@/lib/textStyles";
import { useGetMeQuery } from "@/features/profile/infrastructure/api/ProfileApi";
import { useTranslation } from "react-i18next";

const PatientDetails: React.FC = () => {
  const { t } = useTranslation("patients");
  const { can } = usePatientPermissions();
  const { patientId } = useParams<{ patientId: string }>();
  const { data: profile, isLoading: isLoadingProfile } = useGetMeQuery();
  const [openExamDialog, setOpenExamDialog] = useState(false);
  const [step, setStep] = useState(1);
  const scrollContentRef = React.useRef<HTMLDivElement>(null);
  const [sensoryFormData, setSensoryFormData] = useState<SensoryFormData>(
    initialSensoryFormData,
  );
  const navigate = useNavigate();

  const [neurologicalFormData, setNeurologicalFormData] =
    useState<NeurologicalHistoryFormData>(initialNeurologicalFormData);
  const [behavioralFormData, setBehavioralFormData] =
    useState<BehavioralFormData>(initialBehavioralFormData);

  const [cognitiveFunctionalFormData, setCognitiveFunctionalFormData] =
    useState<CognitiveFunctionalFormData>(initialCognitiveFunctionalFormData);
  const [skippedSteps, setSkippedSteps] = useState<Set<number>>(new Set());
  const [openScheduleDialog, setOpenScheduleDialog] = useState(false);
  const [openScheduleReportReviewDialog, setOpenScheduleReportReviewDialog] =
    useState(false);
  const [showCancelExamConfirm, setShowCancelExamConfirm] = useState(false);

  const [screeningFormData, setScreeningFormData] = useState<ScreeningFormData>(
    initialScreeningFormData,
  );

  const [isEditOpen, setIsEditOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isStopScreeningOpen, setIsStopScreeningOpen] = useState(false);
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const [isCreateAssessmentOpen, setIsCreateAssessmentOpen] = useState(false);
  const [currentAssessmentId, setCurrentAssessmentId] = useState<string>("");
  // Freshest assessment data from the most recent status-changing mutation
  // (start/submit/cancel exam, start/complete report review). Used as a
  // stand-in for the assessment-detail query's cached data until that query
  // actually catches up — the query's id/status can lag a beat behind the
  // mutation response, which otherwise left the UI showing the pre-mutation
  // status until a manual page refresh.
  const [overrideAssessment, setOverrideAssessment] =
    useState<AssessmentDetailDTO | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [isCancellingClinicalExam, setIsCancellingClinicalExam] =
    useState(false);
  const [isStartingClinicalExam, setIsStartingClinicalExam] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(interval);
  }, []);

  // Fetch patient details
  const { data: patient, isLoading: isLoadingPatient } = useGetPatientByIdQuery(
    patientId || "",
    { skip: !patientId },
  );

  // Fetch patient assessments
  const {
    data: assessments,
    isLoading: isLoadingAssessments,
    isError: isAssessmentsError,
    error: assessmentsError,
    refetch: refetchAssessments,
  } = useGetPatientAssessmentsQuery(patientId || "", {
    skip: !patientId,
  });

  // Find ongoing/current assessment
  const ongoingAssessment = assessments?.find(
    (assessment) => assessment.isCurrentAssessment === true,
  );

  const previousAssessments: Assessment[] =
    assessments?.filter(
      (assessment) => assessment.isCurrentAssessment !== true,
    ) ?? [];
  const assessmentIds = React.useMemo(
    () => assessments?.map((assessment) => assessment.id) ?? [],
    [assessments],
  );
  const assessmentIdsKey = assessmentIds.join("|");
  const firstPreviousAssessmentId = previousAssessments[0]?.id;
  const defaultAssessmentId = ongoingAssessment?.id ?? firstPreviousAssessmentId ?? "";

  // Parent-level actions are patient-scoped and must track the backend current assessment.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (currentAssessmentId === defaultAssessmentId) {
      return;
    }

    setCurrentAssessmentId(defaultAssessmentId);
  }, [
    assessmentIdsKey,
    defaultAssessmentId,
    currentAssessmentId,
  ]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Fetch current assessment details
  const { data: currentAssessmentDetail, refetch: refetchAssessmentDetail } =
    useGetPatientAssessmentDetailQuery(
      {
        patientId: patientId as string,
        assessmentId: currentAssessmentId,
      },
      { skip: !patientId || !currentAssessmentId },
    );

  const selectedAssessment = currentAssessmentDetail?.data?.assessment;
  const isCurrentAssessmentDetailReady =
    selectedAssessment?.id === currentAssessmentId;
  const assessment =
    overrideAssessment?.id === currentAssessmentId
      ? overrideAssessment
      : isCurrentAssessmentDetailReady
        ? selectedAssessment
        : undefined;
  const assessmentStatus = assessment?.status;

  // Once the query's own cached data catches up with the override (same id
  // and status), drop the override so later background refetches take over
  // as the source of truth again.
  useEffect(() => {
    if (
      overrideAssessment &&
      selectedAssessment?.id === overrideAssessment.id &&
      selectedAssessment.status === overrideAssessment.status
    ) {
      setOverrideAssessment(null);
    }
  }, [selectedAssessment, overrideAssessment]);
  const reportReviewScheduledStart =
    assessment?.report_review_scheduled_start_at;
  const reportReviewScheduledEnd =
    assessment?.report_review_scheduled_end_at;
  const isReportReviewWindowActive =
    (assessmentStatus === "RR_SCHEDULED" ||
      assessmentStatus === "RR_RESCHEDULED") &&
    isAppointmentInProgress(
      reportReviewScheduledStart,
      reportReviewScheduledEnd,
      now,
    );
  const displayAssessmentStatus = isReportReviewWindowActive
    ? "RR_IN_PROGRESS"
    : assessmentStatus;
  const getRecommendedDoctorIds = (
    doctors: { id: string }[] | null | undefined,
  ) => doctors?.map((doctor) => doctor.id) ?? [];
  const reportReviewDoctorIds = getRecommendedDoctorIds(
    assessment?.report_review_recommended_doctors,
  );
  const isCurrentUserAssignedTo = (doctorIds: string[]) =>
    !!profile?.id && doctorIds.some((doctorId) => doctorId === profile.id);
  const isAssignedReportReviewDoctor =
    isCurrentUserAssignedTo(reportReviewDoctorIds);
  const isReportReviewStartable = isReportReviewWindowActive;
  const isReportReviewCompletable = assessmentStatus === "RR_IN_PROGRESS";
  const canStartReportReview =
    isReportReviewStartable && isAssignedReportReviewDoctor;
  const canCompleteReportReview =
    isReportReviewCompletable && isAssignedReportReviewDoctor;

  useEffect(() => {
    if (!assessmentStatus || !REPORT_POLLING_STATUSES.has(assessmentStatus)) {
      return;
    }
    const interval = setInterval(() => {
      void refetchAssessmentDetail();
    }, 15_000);
    return () => clearInterval(interval);
  }, [assessmentStatus, refetchAssessmentDetail]);

  // Mutations
  const [createAccessibilitySensory, { isLoading: isSubmittingAccessibility }] =
    useCreateAccessibilitySensoryMutation();
  const [submitCaregiverQuestionnaire, { isLoading: isSubmittingCaregiver }] =
    useSubmitCaregiverQuestionnaireMutation();
  const [createAssessment] = useCreateCurrentAssessmentMutation();

  const isSubmitting = isSubmittingAccessibility || isSubmittingCaregiver;
  const isLoading = isLoadingPatient || isLoadingAssessments;

  // Feed a status-changing mutation's own response straight into local state
  // so the UI reflects it on the very next render, instead of waiting on the
  // assessment-detail query to independently catch up.
  const applyAssessmentUpdate = (updated: AssessmentDetailDTO) => {
    setOverrideAssessment(updated);
    setCurrentAssessmentId(updated.id);
  };

  const getInitials = (name: string) =>
    name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  // Severe visual impairment rules out screening: the clinical exam can still be
  // submitted, but the patient must not move on to the screening stage.
  const isScreeningBlockedByVision = sensoryFormData.visionStatus === "severe";

  const handleNext = () => {
    setSkippedSteps((prev) => {
      const next = new Set(prev);
      next.delete(step);
      return next;
    });
    setStep((prev) => Math.min(prev + 1, 5));
    scrollContentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSkip = (stepNumber: number) => {
    setSkippedSteps((prev) => new Set([...prev, stepNumber]));

    if (stepNumber === 2) {
      setNeurologicalFormData(initialNeurologicalFormData);
    } else if (stepNumber === 3) {
      setBehavioralFormData(initialBehavioralFormData);
    } else if (stepNumber === 4) {
      setCognitiveFunctionalFormData(initialCognitiveFunctionalFormData);
    } else if (stepNumber === 5) {
      setScreeningFormData(initialScreeningFormData);
    }

    setStep((prev) => Math.min(prev + 1, 5));
    scrollContentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    setStep((prev) => Math.max(prev - 1, 1));
    scrollContentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleClose = () => {
    setOpenExamDialog(false);
    setStep(1);
    setSensoryFormData(initialSensoryFormData);
    setNeurologicalFormData(initialNeurologicalFormData);
    setBehavioralFormData(initialBehavioralFormData);
    setCognitiveFunctionalFormData(initialCognitiveFunctionalFormData);
    setScreeningFormData(initialScreeningFormData);
    setSkippedSteps(new Set());
  };

  const handleCancelSuccess = () => {
    refetchAssessments();
    refetchAssessmentDetail();
  };

  const handleCancelClinicalExamination = async () => {
    if (!patientId) {
      toast.error(t("patientDetails.patientIdMissing"));
      return;
    }

    try {
      setIsCancellingClinicalExam(true);
      const updated = await createAssessment({
        patientId,
        data: {
          status: "CC_CANCELLED",
          reason: "Cancelled from portal",
        },
      }).unwrap();
      applyAssessmentUpdate(updated);
      toast.success(t("patientDetails.clinicalExaminationCancelledToast"));
      setShowCancelExamConfirm(false);
      handleClose();
      refetchAssessments();
      refetchAssessmentDetail();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.error(
        error?.data?.message ??
          error?.data?.detail ??
          t("patientDetails.cancelClinicalExamFailed"),
      );
    } finally {
      setIsCancellingClinicalExam(false);
    }
  };

  const handleSubmit = async () => {
    if (!patientId || !currentAssessmentId) {
      toast.error(t("patientDetails.patientOrAssessmentIdMissing"));
      return;
    }

    const assignedClinicalContextDoctorIds = getRecommendedDoctorIds(
      assessment?.clinical_context_recommended_doctors,
    );

    if (!isCurrentUserAssignedTo(assignedClinicalContextDoctorIds)) {
      toast.error(t("patientDetails.onlyAssignedDoctorSubmit"));
      return;
    }

    try {
      // Try to submit sensory data first
      await createAccessibilitySensory({
        assessmentId: currentAssessmentId,
        data: sensoryFormData,
        skipped: skippedSteps.has(1),
      }).unwrap();
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err = error as any;
      // If it's a 409 conflict, it means data already exists - this is fine, continue
      if (err?.status === 409 || err?.data?.status_code === 409) {
        console.log(
          "Sensory data already exists, continuing with caregiver questionnaire...",
        );
        // Don't return here - continue with caregiver questionnaire
      } else {
        // For any other error, show error and stop submission
        const errorMessage =
          err?.data?.message || t("patientDetails.sensorySaveFailed");
        toast.error(errorMessage);
        console.error("Sensory submission failed:", error);
        return; // Only return on non-409 errors
      }
    }

    // Submit caregiver questionnaire; skipped steps send null values via mapper.
    try {
      await submitCaregiverQuestionnaire({
        assessmentId: currentAssessmentId,
        neurological: neurologicalFormData,
        behavioral: behavioralFormData,
        cognitiveFunctional: cognitiveFunctionalFormData,
        skippedSteps: {
          neurological: skippedSteps.has(2),
          behavioral: skippedSteps.has(3),
          cognitiveFunctional: skippedSteps.has(4),
        },
      }).unwrap();
      console.log("Caregiver questionnaire submitted successfully");
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err = error as any;
      if (err?.status === 409 || err?.data?.status_code === 409) {
        console.log("Caregiver questionnaire already exists, continuing...");
      } else {
        const errorMessage =
          err?.data?.message ||
          t("patientDetails.caregiverQuestionnaireSaveFailed");
        toast.error(errorMessage);
        console.error("Caregiver questionnaire submission failed:", error);
        return;
      }
    }

    // Finally, update the assessment status
    try {
      const updatedAssessment = await createAssessment({
        patientId,
        data: {
          status: "CC_COMPLETED",
          screening_required: isScreeningBlockedByVision
            ? false
            : skippedSteps.has(5)
              ? null
              : (screeningFormData.isScreeningRequired ?? true),
          recommended_screening_doctor_user_ids:
            isScreeningBlockedByVision || skippedSteps.has(5)
              ? null
              : screeningFormData.selectedDoctors,
        },
      }).unwrap();

      applyAssessmentUpdate(updatedAssessment);
      await Promise.all([refetchAssessments(), refetchAssessmentDetail()]);
      toast.success(t("patientDetails.clinicalExamSubmitSuccess"));
      handleClose();
    } catch (error: unknown) {
      const errorMessage =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (error as any)?.data?.message ||
        t("patientDetails.clinicalExamSubmitFailed");
      toast.error(errorMessage);
      console.error("Failed to submit clinical examination:", error);
    }
  };

  const calculateAge = (dobString: string) => {
    const today = new Date();
    const dob = new Date(dobString);
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  };

  const handleStartReportReview = async () => {
    if (!patientId) {
      toast.error(t("patientDetails.patientIdMissing"));
      return;
    }

    if (!isAssignedReportReviewDoctor) {
      toast.error(t("patientDetails.onlyAssignedDoctorStartReportReview"));
      return;
    }

    if (!isReportReviewStartable) {
      toast.error(t("patientDetails.reportReviewOnlyScheduledTime"));
      return;
    }

    try {
      const updated = await createAssessment({
        patientId,
        data: { status: "RR_IN_PROGRESS" },
      }).unwrap();
      applyAssessmentUpdate(updated);
      toast.success(t("patientDetails.reportReviewStarted"));
      refetchAssessments();
      refetchAssessmentDetail();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.error(
        error?.data?.message || t("patientDetails.startReportReviewFailed"),
      );
    }
  };

  const handleCompleteReportReview = async () => {
    if (!patientId) {
      toast.error(t("patientDetails.patientIdMissing"));
      return;
    }

    if (!isAssignedReportReviewDoctor) {
      toast.error(t("patientDetails.onlyAssignedDoctorCompleteReportReview"));
      return;
    }

    if (!isReportReviewCompletable) {
      toast.error(t("patientDetails.startReportReviewBeforeComplete"));
      return;
    }

    try {
      const updated = await createAssessment({
        patientId,
        data: { status: "RR_COMPLETED" },
      }).unwrap();
      applyAssessmentUpdate(updated);
      toast.success(t("patientDetails.reportReviewCompleted"));
      refetchAssessments();
      refetchAssessmentDetail();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.error(
        error?.data?.message || t("patientDetails.completeReportReviewFailed"),
      );
    }
  };

  const handleStartClinicalExamination = async () => {
    if (!patientId) {
      toast.error(t("patientDetails.patientIdMissing"));
      return;
    }

    const assignedClinicalContextDoctorIds = getRecommendedDoctorIds(
      assessment?.clinical_context_recommended_doctors,
    );

    if (!isCurrentUserAssignedTo(assignedClinicalContextDoctorIds)) {
      toast.error(t("patientDetails.onlyAssignedDoctorStartExam"));
      return;
    }

    if (assessment?.status === "CC_IN_SESSION") {
      setOpenExamDialog(true);
      return;
    }

    try {
      setIsStartingClinicalExam(true);
      const updated = await createAssessment({
        patientId,
        data: { status: "CC_IN_SESSION" },
      }).unwrap();
      applyAssessmentUpdate(updated);
      refetchAssessments();
      refetchAssessmentDetail();
      setOpenExamDialog(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.error(
        error?.data?.message || t("patientDetails.startExaminationFailed"),
      );
    } finally {
      setIsStartingClinicalExam(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleEditPatient = (rowPatient: any) => {
    setSelectedPatient(rowPatient);
    setIsEditOpen(true);
  };

  const handleDeletePatient = () => {
    setSelectedPatient(patient);
    setIsDeleteOpen(true);
  };

  const shouldShowClinicalExamination = () => {
    const status = assessment?.status;

    if (!status) return false;

    if (HIDE_CLINICAL_EXAM_STATUSES.has(status)) return false;

    return (
      status === "CC_SCHEDULED" ||
      status === "CC_RESCHEDULED" ||
      status === "CC_IN_SESSION"
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin h-8 w-8 text-gray-500" />
      </div>
    );
  }

  if (isAssessmentsError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-destructive-text mb-4">
            {t("patientDetails.loadAssessmentsError")}
          </p>
          <p className="text-sm text-gray-500">
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            {(assessmentsError as any)?.data?.message ||
              t("patientDetails.pleaseTryAgain")}
          </p>
          <Button onClick={() => window.location.reload()} className="mt-4">
            {t("patientDetails.retry")}
          </Button>
        </div>
      </div>
    );
  }

  if (!patient) return null;

  const isDisabled =
    assessmentStatus != null &&
    DISABLE_ACTION_STATUSES.has(assessmentStatus);
  const clinicalScheduledStart =
    assessment?.clinical_context_scheduled_start_at;
  const clinicalContextDoctorIds = getRecommendedDoctorIds(
    assessment?.clinical_context_recommended_doctors,
  );
  const isAssignedClinicalContextDoctor =
    isCurrentUserAssignedTo(clinicalContextDoctorIds);
  const isClinicalExaminationInProgress = assessmentStatus === "CC_IN_SESSION";
  const canStartClinicalExamination =
    isAssignedClinicalContextDoctor &&
    (isClinicalExaminationInProgress ||
      hasAppointmentStarted(clinicalScheduledStart, now));
  const timeUntilClinicalExamStart = formatTimeUntilStart(
    clinicalScheduledStart,
    now,
  );
  const isClinicalContextAppt =
    assessmentStatus === "CC_SCHEDULED" ||
    assessmentStatus === "CC_RESCHEDULED" ||
    assessmentStatus === "CC_IN_SESSION";
  const isReportReviewAppt = assessmentStatus?.startsWith("RR_");
  const isCancelledAppointment =
    assessmentStatus === "CC_CANCELLED" ||
    assessmentStatus === "SCR_CANCELLED" ||
    assessmentStatus === "RR_CANCELLED";
  const isReportGenerationStatus =
    (assessmentStatus != null && REPORT_PENDING_STATUSES.has(assessmentStatus)) ||
    assessmentStatus === REPORT_GENERATING_STATUS ||
    assessmentStatus === REPORT_FAILED_STATUS;
  const cancelledAppointmentLabel =
    assessmentStatus === "CC_CANCELLED"
      ? t("patientDetails.typeClinicalContext")
      : assessmentStatus === "RR_CANCELLED"
        ? t("patientDetails.typeReportReview")
        : t("patientDetails.typeScreening");
  const hideAppointmentActions =
    isReportGenerationStatus ||
    (assessmentStatus != null &&
      NON_RETAKE_SCREENING_STATUSES.has(assessmentStatus)) ||
    !ongoingAssessment;
  const hasMultipleAssessments = (assessments?.length ?? 0) > 1;
  const canCreateNewAssessment = !ongoingAssessment && (assessments?.length ?? 0) > 0;
  const createAssessmentReferenceId =
    previousAssessments[0]?.id ?? currentAssessmentId;
  const isAssessmentDetailPending =
    !!currentAssessmentId && !isCurrentAssessmentDetailReady;
  const assessmentDetailLoadingCard = (
    <Card className="mb-6 p-6 text-center sm:p-10">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
        <p style={{ ...textStyles.body, color: "#5F646F" }}>
          {t("patientDetails.loadingAssessmentDetails")}
        </p>
      </div>
    </Card>
  );
  const clinicalExaminationStartCard = (
    <Card className="mb-6 p-6 text-center sm:p-10">
      <div className="flex flex-col items-center gap-4">
        <AssignmentIcon />
        <h3 style={textStyles.heading3}>
          {t("patientDetails.startClinicalExaminationTitle")}
        </h3>
        <p
          className="max-w-[300px]"
          style={{ ...textStyles.body, color: "#5F646F" }}
        >
          {t("patientDetails.startClinicalExaminationBody")}
        </p>
        <PermissionGuard allowed={can("test_create")}>
          <div className="flex flex-col items-center gap-2">
            <Button
              onClick={handleStartClinicalExamination}
              disabled={!canStartClinicalExamination || isStartingClinicalExam}
              loading={isStartingClinicalExam}
              className="h-10 bg-primary px-4 text-md font-semibold text-white hover:bg-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isClinicalExaminationInProgress
                ? t("patientDetails.continueClinicalExamination")
                : t("patientDetails.addClinicalExamination")}
            </Button>
            {isAssignedClinicalContextDoctor &&
              !canStartClinicalExamination &&
              timeUntilClinicalExamStart && (
                <p style={{ ...textStyles.body, color: "#5F646F" }}>
                  {t("patientDetails.startsIn", {
                    time: timeUntilClinicalExamStart,
                  })}
                </p>
              )}
            {!isLoadingProfile && !isAssignedClinicalContextDoctor && (
              <p style={{ ...textStyles.body, color: "#5F646F" }}>
                {t("patientDetails.onlyAssignedDoctorCanStart")}
              </p>
            )}
          </div>
        </PermissionGuard>
      </div>
    </Card>
  );

  const clinicalExaminationCancelledCard = (
    <Card className="mb-6 p-6 text-center sm:p-10">
      <div className="flex flex-col items-center gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[color-mix(in_oklab,var(--destructive-text)_10%,white)]">
          <CircleX className="h-7 w-7 text-destructive-text" />
        </div>
        <h3 style={textStyles.heading3}>
          {t("patientDetails.clinicalExaminationCancelledTitle")}
        </h3>
        <p
          className="max-w-[320px]"
          style={{ ...textStyles.body, color: "#5F646F" }}
        >
          {t("patientDetails.clinicalExaminationCancelledBody")}
        </p>
        <PermissionGuard allowed={can("appointment_update")}>
          <Button
            variant="outline"
            className="border-primary px-4 text-primary hover:bg-white hover:text-primary cursor-pointer"
            onClick={() => setIsRescheduleOpen(true)}
          >
            {t("patientDetails.rescheduleAppointment")}
          </Button>
        </PermissionGuard>
      </div>
    </Card>
  );

  return (
    <div className="min-h-full overflow-y-auto px-4 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] pt-4 sm:px-5 lg:px-6 lg:pb-4">
      <p className="mb-2 flex min-w-0 items-center gap-1 text-sm text-gray-500">
        <span
          className="shrink-0 hover:text-primary cursor-pointer"
          onClick={() => navigate("/patients")}
        >
          {t("patientDetails.breadcrumbPatients")}
        </span>
        <span className="shrink-0">/</span>
        <span
          className="min-w-0 truncate"
          title={patient.name.length > 20 ? patient.name : undefined}
        >
          {patient.name}
        </span>
      </p>

      {/* Top Patient Card */}
      <Card className="mb-6 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 gap-3 sm:gap-4">
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#F2F5FF] uppercase sm:h-14 sm:w-14"
              style={textStyles.heading2}
            >
              {getInitials(patient.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 flex-col gap-1 pr-1 sm:flex-row sm:items-baseline sm:gap-2 sm:pr-0">
                <h2
                  className="m-0 min-w-0 truncate"
                  title={patient.name.length > 20 ? patient.name : undefined}
                >
                  <span className="block truncate" style={textStyles.heading2}>
                    {patient.name}
                  </span>
                </h2>
                <div className="shrink-0">
                  <AssessmentStatusPill
                    status={displayAssessmentStatus}
                    reason={getStatusReason(assessment)}
                  />
                </div>
              </div>

              <div
                className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1"
                style={{ ...textStyles.body, color: "#5F646F" }}
              >
                <span className="flex items-center gap-1">
                  <span aria-hidden="true">•</span>
                  <CallIcon />
                  <span>{patient.phone}</span>
                </span>
                <span className="flex items-center gap-1">
                  <span aria-hidden="true">•</span>
                  <MailIcon />
                  <span>{patient.email || t("patientDetails.noEmail")}</span>
                </span>
                <span className="flex items-center gap-1">
                  <span aria-hidden="true">•</span>
                  <UserIcon />
                  <span>
                    {patient.gender},{" "}
                    {patient.dateOfBirth
                      ? calculateAge(patient.dateOfBirth)
                      : patient.age ?? "—"}
                  </span>
                </span>
                {patient.language && (
                  <span className="flex min-w-0 max-w-[180px] items-center gap-1">
                    <span aria-hidden="true">•</span>
                    <span
                      className="truncate"
                      title={
                        patient.language.length > 20
                          ? patient.language
                          : undefined
                      }
                    >
                      {patient.language}
                    </span>
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <span aria-hidden="true">•</span>
                  <span>{patient.ethnicity}</span>
                </span>
                <span className="flex items-center gap-1">
                  <span aria-hidden="true">•</span>
                  <span>
                    {t("patientDetails.createdOn", {
                      date: new Date(patient.createdAt).toLocaleDateString(
                        "en-GB",
                        { day: "2-digit", month: "short" },
                      ),
                    })}
                  </span>
                </span>
              </div>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="-mr-1 -mt-1 h-8 w-8 shrink-0 cursor-pointer sm:mr-0 sm:mt-0"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {can("patient_update") ? (
                <DropdownMenuItem
                  onClick={() => handleEditPatient(patient)}
                  className="cursor-pointer"
                >
                  <EditIcon className="mr-2 h-4 w-4" />
                  {t("patientDetails.edit")}
                </DropdownMenuItem>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center rounded-sm px-2 py-1.5 text-sm opacity-50 cursor-not-allowed select-none">
                      <EditIcon className="mr-2 h-4 w-4" />
                      {t("patientDetails.edit")}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    {t("patientDetails.accessDenied")}
                  </TooltipContent>
                </Tooltip>
              )}
              {can("patient_delete") ? (
                <DropdownMenuItem
                  onClick={handleDeletePatient}
                  className="text-destructive-text hover:text-destructive-text cursor-pointer focus:text-destructive-text"
                >
                  <DeleteIcon className="mr-2 h-4 w-4 text-destructive-text hover:text-destructive-text" />
                  {t("patientDetails.delete")}
                </DropdownMenuItem>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center rounded-sm px-2 py-1.5 text-sm opacity-50 cursor-not-allowed select-none text-destructive-text">
                      <DeleteIcon className="mr-2 h-4 w-4" />
                      {t("patientDetails.delete")}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    {t("patientDetails.accessDenied")}
                  </TooltipContent>
                </Tooltip>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <Separator className="my-1" />

        <div
          className="flex flex-wrap items-center gap-1"
          style={{ ...textStyles.body, color: "#1B1D21" }}
        >
          {t("patientDetails.caregiverInfo")}{" "}
          {patient.caregiverName?.trim() && (
            <div className="w-7 h-7 rounded-full bg-[#F2F5FF] flex items-center justify-center font-bold ml-2">
              {getInitials(patient.caregiverName)}
            </div>
          )}{" "}
          {patient.caregiverName || "-"} • {patient.caregiverNumber || "-"}
        </div>

        <Separator className="my-1" />

        {isAssessmentDetailPending ? (
          <div className="flex flex-col items-center gap-3 bg-white py-4">
            <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
            <p style={{ ...textStyles.body, color: "#5F646F" }}>
              {t("patientDetails.loadingAssessmentDetails")}
            </p>
          </div>
        ) : canCreateNewAssessment ? (
          <div className="flex flex-col gap-4 bg-white sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 flex-col gap-1">
              <p style={{ ...textStyles.body, color: "#1B1D21", fontWeight: 700 }}>
                {t("patientDetails.scheduleNewAssessmentTitle")}
              </p>
              <p style={{ ...textStyles.body, color: "#1B1D21" }}>
                {assessmentStatus != null &&
                CREATE_NEW_ASSESSMENT_STATUSES.has(assessmentStatus)
                  ? assessmentStatus === "RR_COMPLETED"
                    ? t("patientDetails.scheduleNewAssessmentCompleteBody")
                    : t("patientDetails.scheduleNewAssessmentRetakeBody")
                  : t("patientDetails.scheduleNewAssessmentDefaultBody")}
              </p>
            </div>
            <PermissionGuard allowed={can("appointment_create")}>
              <Button
                className="w-full bg-primary px-4 text-white hover:bg-primary cursor-pointer sm:w-auto"
                onClick={() => setIsCreateAssessmentOpen(true)}
              >
                {t("patientDetails.scheduleNewAssessmentButton")}
              </Button>
            </PermissionGuard>
          </div>
        ) : isCancelledAppointment ? (
          <div className="flex flex-col gap-3 rounded-lg border border-destructive-text bg-[color-mix(in_oklab,var(--destructive-text)_10%,white)] p-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <CircleX className="mt-0.5 size-5 shrink-0 text-destructive-text" />
              <div className="min-w-0">
                <h3 className="text-[length:var(--type-body-font-size)] leading-[var(--type-body-line-height)] text-destructive-text [font-weight:700]">
                  {t("patientDetails.appointmentCancelledTitle", {
                    type: cancelledAppointmentLabel,
                  })}
                </h3>
                <p className="text-[length:var(--type-body-font-size)] leading-[var(--type-body-line-height)] text-destructive-text [font-weight:400]">
                  {t("patientDetails.appointmentCancelledBody")}
                </p>
              </div>
            </div>
            <PermissionGuard allowed={can("appointment_update")}>
              <Button
                variant="outline"
                className="w-full border-primary bg-white px-4 text-primary hover:bg-white hover:text-primary cursor-pointer sm:w-auto"
                onClick={() => {
                  if (assessmentStatus === "RR_CANCELLED") {
                    setOpenScheduleReportReviewDialog(true);
                  } else {
                    setIsRescheduleOpen(true);
                  }
                }}
              >
                {t("patientDetails.reschedule")}
              </Button>
            </PermissionGuard>
          </div>
        ) : patientId &&
          currentAssessmentId &&
          assessmentStatus &&
          isReportGenerationStatus ? (
          <ReportGenerationBanner
            patientId={patientId}
            assessmentId={currentAssessmentId}
            status={assessmentStatus}
            onRetrySuccess={() => {
              refetchAssessments();
              refetchAssessmentDetail();
            }}
          />
        ) : assessmentStatus === "SCR_REPORT_GENERATED" ? (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 flex-col gap-1">
              <p style={{ ...textStyles.body, fontWeight: 700 }}>
                {t("patientDetails.reportReviewAppointmentTitle")}
              </p>
              <p style={{ ...textStyles.body, color: "#5F646F" }}>
                {t("patientDetails.reportReviewAppointmentBody")}
              </p>
            </div>
            <PermissionGuard allowed={can("appointment_create")}>
              <Button
                variant="outline"
                className="w-full border-primary bg-primary text-white hover:bg-primary hover:text-white cursor-pointer sm:w-auto"
                onClick={() => setOpenScheduleReportReviewDialog(true)}
              >
                {t("patientDetails.schedule")}
              </Button>
            </PermissionGuard>
          </div>
        ) : (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 flex-col gap-1">
              <p style={{ ...textStyles.body, fontWeight: 700 }}>
                {isClinicalContextAppt
                  ? t("patientDetails.clinicalContextAppointmentTitle")
                  : isReportReviewAppt
                    ? t("patientDetails.reportReviewAppointmentTitle")
                    : t("patientDetails.screeningAppointmentTitle")}
              </p>
              {isClinicalContextAppt ? (
                <div className="flex min-w-0 items-start gap-2">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F2F5FF] font-bold">
                    {getInitials(
                      assessment?.clinical_context_recommended_doctors?.[0]?.name ||
                        "Dr",
                    )}
                  </div>
                  <div className="flex min-w-0 flex-col">
                    <p
                      className="break-words"
                      style={{ ...textStyles.body, color: "#5F646F" }}
                    >
                      {
                        assessment?.clinical_context_recommended_doctors?.[0]?.name
                      }{" "}
                      -{" "}
                      {
                        assessment?.clinical_context_recommended_doctors?.[0]
                          ?.designation
                      }
                    </p>
                    <p style={{ ...textStyles.body, color: "#5F646F" }}>
                      {formatDateTimeShort(clinicalScheduledStart)}
                    </p>
                  </div>
                </div>
              ) : isReportReviewAppt ? (
                <div className="flex min-w-0 items-start gap-2">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F2F5FF] font-bold">
                    {getInitials(
                      assessment?.report_review_recommended_doctors?.[0]?.name || "Dr",
                    )}
                  </div>
                  <div className="flex min-w-0 flex-col">
                    <p
                      className="break-words"
                      style={{ ...textStyles.body, color: "#5F646F" }}
                    >
                      {
                        assessment?.report_review_recommended_doctors?.[0]?.name
                      }{" "}
                      -{" "}
                      {
                        assessment?.report_review_recommended_doctors?.[0]
                          ?.designation
                      }
                    </p>
                    <p style={{ ...textStyles.body, color: "#5F646F" }}>
                      {formatDateTimeShort(reportReviewScheduledStart)}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex min-w-0 items-start gap-2">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F2F5FF] font-bold">
                    {getInitials(
                      assessment?.screening_recommended_doctors?.[0]?.name || "Dr",
                    )}
                  </div>
                  <div className="flex min-w-0 flex-col">
                    <p
                      className="break-words"
                      style={{ ...textStyles.body, color: "#5F646F" }}
                    >
                      {
                        assessment?.screening_recommended_doctors?.[0]?.name
                      }{" "}
                      -{" "}
                      {
                        assessment?.screening_recommended_doctors?.[0]?.designation
                      }
                    </p>
                    <p style={{ ...textStyles.body, color: "#5F646F" }}>
                      {formatDateTimeShort(assessment?.screening_scheduled_start_at)}
                    </p>
                  </div>
                </div>
              )}
            </div>
            {assessmentStatus === "CC_COMPLETED" ? (
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                <PermissionGuard allowed={can("appointment_create")}>
                  <Button
                    variant="outline"
                    className="w-full border-primary bg-primary text-white hover:bg-primary hover:text-white cursor-pointer sm:w-auto"
                    onClick={() => setOpenScheduleDialog(true)}
                  >
                    {t("patientDetails.schedule")}
                  </Button>
                </PermissionGuard>
              </div>
            ) : isReportReviewStartable || isReportReviewCompletable ? (
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                <PermissionGuard allowed={can("appointment_update")}>
                  <Button
                    className="w-full bg-primary px-4 text-white hover:bg-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                    disabled={
                      isReportReviewCompletable
                        ? !canCompleteReportReview
                        : !canStartReportReview
                    }
                    onClick={
                      isReportReviewCompletable
                        ? handleCompleteReportReview
                        : handleStartReportReview
                    }
                  >
                    {isReportReviewCompletable
                      ? t("patientDetails.markReportReviewComplete")
                      : t("patientDetails.startReportReview")}
                  </Button>
                </PermissionGuard>
                <PermissionGuard allowed={can("appointment_delete")}>
                  <Button
                    variant="outline"
                    className="w-full border-[#E53935] px-4 text-[#E53935] hover:bg-white hover:text-[#E53935] cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                    disabled={!isAssignedReportReviewDoctor}
                    onClick={() => setIsCancelOpen(true)}
                  >
                    {t("patientDetails.cancelAppointment")}
                  </Button>
                </PermissionGuard>
                {!isLoadingProfile && !isAssignedReportReviewDoctor && (
                  <p style={{ ...textStyles.body, color: "#5F646F" }}>
                    {t("patientDetails.onlyAssignedDoctorCanAction", {
                      action: isReportReviewCompletable
                        ? t("patientDetails.complete")
                        : t("patientDetails.start"),
                    })}
                  </p>
                )}
              </div>
            ) : assessmentStatus === "SCR_IN_PROGRESS" && !hideAppointmentActions ? (
              // A running screening can't be rescheduled or cancelled — only stopped.
              <PermissionGuard allowed={can("appointment_delete")}>
                <Button
                  variant="outline"
                  className="w-full border-[#E53935] px-4 text-[#E53935] hover:bg-white hover:text-[#E53935] cursor-pointer sm:w-auto"
                  onClick={() => setIsStopScreeningOpen(true)}
                >
                  {t("stopScreeningDialog.trigger")}
                </Button>
              </PermissionGuard>
            ) : (
              !hideAppointmentActions && (
                <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                  <PermissionGuard allowed={can("appointment_update")}>
                    <Button
                      variant="outline"
                      className="w-full border-primary bg-white px-4 text-primary hover:bg-white hover:text-primary cursor-pointer sm:w-auto"
                      disabled={
                        isDisabled ||
                        (isReportReviewAppt && !isAssignedReportReviewDoctor)
                      }
                      onClick={() => setIsRescheduleOpen(true)}
                    >
                      {t("patientDetails.reschedule")}
                    </Button>
                  </PermissionGuard>
                  <PermissionGuard allowed={can("appointment_delete")}>
                    <Button
                      variant="outline"
                      className="w-full border-[#E53935] px-4 text-[#E53935] hover:bg-white hover:text-[#E53935] cursor-pointer sm:w-auto"
                      disabled={
                        isDisabled ||
                        (isReportReviewAppt && !isAssignedReportReviewDoctor)
                      }
                      onClick={() => setIsCancelOpen(true)}
                    >
                      {t("patientDetails.cancelAppointment")}
                    </Button>
                  </PermissionGuard>
                </div>
              )
            )}
          </div>
        )}
      </Card>

      {/* Clinical Context Section */}
      {!hasMultipleAssessments &&
        shouldShowClinicalExamination() &&
        clinicalExaminationStartCard}

      {!hasMultipleAssessments &&
        assessmentStatus === "CC_CANCELLED" &&
        clinicalExaminationCancelledCard}

      {/* Single assessment — no tabs */}
      {!hasMultipleAssessments &&
        !shouldShowClinicalExamination() &&
        assessmentStatus !== "CC_CANCELLED" &&
        (assessments?.length ?? 0) === 1 &&
        isCurrentAssessmentDetailReady &&
        currentAssessmentId && (
          <ClinicalExaminationDetails assessmentId={currentAssessmentId} />
        )}

      {/* Multiple assessments — Current / Previous tabs */}
      {hasMultipleAssessments && (
        <AssessmentList
          ongoingAssessment={ongoingAssessment}
          previousAssessments={previousAssessments}
          ongoingContent={
            isAssessmentDetailPending
              ? assessmentDetailLoadingCard
              : shouldShowClinicalExamination()
              ? clinicalExaminationStartCard
              : assessmentStatus === "CC_CANCELLED"
              ? clinicalExaminationCancelledCard
              : undefined
          }
        />
      )}

      {/* Dialog Components */}
      <Dialog
        open={openExamDialog && isAssignedClinicalContextDoctor}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setShowCancelExamConfirm(true);
          }
        }}
      >
        <DialogContent
          className="flex h-[calc(100dvh-1rem)] min-h-0 flex-col overflow-hidden p-0 sm:h-[85vh] sm:max-w-[650px]"
          onEscapeKeyDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
        >
          <DialogHeader className="flex-shrink-0 px-4 pt-4 sm:px-6">
            <DialogTitle className="text-xl font-bold">
              {t("patientDetails.clinicalExaminationDialogTitle")}
            </DialogTitle>
          </DialogHeader>

          <div
            ref={scrollContentRef}
            className="no-scrollbar min-h-0 flex-1 overflow-y-auto border-y-2 bg-[#F4F6F9] px-4 py-2 sm:px-6"
          >
            {step === 1 && (
              <Step1SensoryAssessment
                formData={sensoryFormData}
                onFormDataChange={setSensoryFormData}
              />
            )}
            {step === 2 && (
              <Step2NeurologicalHistory
                formData={neurologicalFormData}
                onFormDataChange={setNeurologicalFormData}
                onSkip={() => handleSkip(2)}
              />
            )}
            {step === 3 && (
              <Step3BehavioralAssessment
                formData={behavioralFormData}
                onFormDataChange={setBehavioralFormData}
                onSkip={() => handleSkip(3)}
              />
            )}
            {step === 4 && (
              <Step4CognitiveFunctional
                formData={cognitiveFunctionalFormData}
                onFormDataChange={setCognitiveFunctionalFormData}
                onSkip={() => handleSkip(4)}
              />
            )}
            {step === 5 && (
              <Step5Screening
                formData={screeningFormData}
                onFormDataChange={setScreeningFormData}
                patientId={patientId ?? ""}
                assessmentId={currentAssessmentId}
                screeningBlocked={isScreeningBlockedByVision}
              />
            )}
          </div>

          <div className="flex-shrink-0 bg-white px-4 pb-4 pt-3 sm:px-6">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                variant="outline"
                onClick={
                  step === 1 ? () => setShowCancelExamConfirm(true) : handleBack
                }
                disabled={isSubmitting}
                className="cursor-pointer py-5 px-4 hover:bg-white"
              >
                {step === 1
                  ? t("patientDetails.cancel")
                  : t("patientDetails.back")}
              </Button>
              <Button
                onClick={step === 5 ? handleSubmit : handleNext}
                disabled={
                  !isAssignedClinicalContextDoctor ||
                  (step === 5 &&
                    !skippedSteps.has(5) &&
                    !isScreeningBlockedByVision &&
                    screeningFormData.isScreeningRequired === true &&
                    screeningFormData.selectedDoctors.length === 0)
                }
                loading={isSubmitting}
                className="bg-primary hover:bg-primary text-white cursor-pointer  py-5 px-4"
              >
                {step === 5
                  ? t("patientDetails.submit")
                  : t("patientDetails.next")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showCancelExamConfirm}
        onOpenChange={setShowCancelExamConfirm}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("patientDetails.cancelExaminationTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            {t("patientDetails.cancelExaminationBody")}
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              className="cursor-pointer hover:bg-white"
              onClick={() => setShowCancelExamConfirm(false)}
            >
              {t("patientDetails.back")}
            </Button>
            <Button
              className="bg-destructive hover:bg-destructive text-white cursor-pointer"
              loading={isCancellingClinicalExam}
              onClick={handleCancelClinicalExamination}
            >
              {t("patientDetails.cancelAndExit")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ScheduleScreeningDialog
        open={openScheduleDialog}
        onClose={() => setOpenScheduleDialog(false)}
        assessmentId={currentAssessmentId}
      />

      <ScheduleReportReviewDialog
        open={openScheduleReportReviewDialog}
        onClose={() => {
          setOpenScheduleReportReviewDialog(false);
          refetchAssessments();
          refetchAssessmentDetail();
        }}
        assessmentId={currentAssessmentId}
      />

      <RescheduleScreeningDialog
        open={isRescheduleOpen}
        onClose={() => setIsRescheduleOpen(false)}
        assessmentId={currentAssessmentId}
        currentStatus={assessment?.status ?? ""}
        currentScheduledStart={
          assessment?.clinical_context_scheduled_start_at ??
          assessment?.screening_scheduled_start_at ??
          assessment?.report_review_scheduled_start_at
        }
      />

      {createAssessmentReferenceId && (
        <RescheduleScreeningDialog
          createMode
          open={isCreateAssessmentOpen}
          onSuccess={(assessmentId) => {
            setCurrentAssessmentId(assessmentId);
            refetchAssessments();
          }}
          onClose={() => {
            setIsCreateAssessmentOpen(false);
          }}
          assessmentId={createAssessmentReferenceId}
          currentStatus="CC_SCHEDULED"
        />
      )}

      <EditPatientDialog
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        patient={selectedPatient}
        onSuccess={() => {
          setIsEditOpen(false);
          refetchAssessments();
        }}
      />

      <DeletePatientDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        patientId={selectedPatient?.id}
        patientName={selectedPatient?.name}
        onSuccess={() => {
          setIsDeleteOpen(false);
          navigate("/patients");
        }}
      />

      <StopScreeningDialog
        open={isStopScreeningOpen}
        onOpenChange={setIsStopScreeningOpen}
        onSuccess={handleCancelSuccess}
        patientId={patientId ?? ""}
      />

      <CancelScreeningApporintmentDialog
        open={isCancelOpen}
        onOpenChange={setIsCancelOpen}
        onSuccess={handleCancelSuccess}
        patientId={patientId ?? ""}
        currentStatus={assessment?.status ?? ""}
        dateTime={(() => {
          const s = assessment;
          if (!s) return undefined;
          const raw = s.status.startsWith("CC_")
            ? s.clinical_context_scheduled_start_at
            : s.status.startsWith("RR_")
              ? s.report_review_scheduled_start_at
              : s.screening_scheduled_start_at;
          return raw ? formatDateTimeShort(raw) : undefined;
        })()}
      />
    </div>
  );
};

export default PatientDetails;
